import * as React from 'react'
import { IconBlock, type Block } from '@basehub/data/replicache/index'

import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'

import * as PropertiesPanel from '../../properties-panel'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { useRepoUndoManager } from '~/replicache/frontend/undo-redo/manager'
import { UpdateBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import { getBlockFocusOp } from '~/replicache/frontend/undo-redo/util'
import { useTabsStore } from '../../tabs/store'
import { useIconLibrary } from './use-icon-library'

export const IconBlockPropertiesPanel = ({
  block,
  parent,
  path,
}: {
  block: IconBlock
  parent: Block
  path: string
}) => {
  const { data } = useRepoReplicache()
  const replicache = data?.replicache
  const undoManager = useRepoUndoManager()
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)
  const sourceBlockId = idToSourceBlockId(block)
  const getInputFocusOp = React.useCallback(
    (id: string) => {
      return getBlockFocusOp({
        block: {
          id: block.id,
          sourceBlockId: block.sourceBlockId,
        },
        preferredTabKey: activeTabKey,
        focusType: 'block:properties-panel',
        elementIdOverride: id,
      })
    },
    [block.id, block.sourceBlockId, activeTabKey]
  )
  const lib = useIconLibrary()
  const libsEntries = lib.data?.libs.items ?? []
  const iconLibraries = (lib.data?.libs.items ?? [])
    .map((l) => ({
      id: l.value,
      title: l._title,
      name: l._title,
    }))
    .filter(Boolean)
  const allowedLibraries: PropertiesPanel.CheckItem[] = libsEntries
    .map((lib) => {
      if (!block.constraints.allowedLibraries) {
        return { id: lib.value, name: lib._title }
      }
      const allowedEntries = Object.entries(block.constraints.allowedLibraries)
      const foundInAllowed = allowedEntries.find(
        ([value]) => value === lib.value
      )
      if (!foundInAllowed) return { id: lib.value, name: lib._title }
      if (!foundInAllowed[1]) return null
      return { id: lib.value, name: lib._title }
    })
    .filter(Boolean)

  return (
    <>
      <PropertiesPanel.PropertiesContainer>
        <PropertiesPanel.Group title="Constraints" spacing="tight">
          <PropertiesPanel.BooleanOption
            name="Is required"
            value={block.constraints.isRequired}
            id={`el-${sourceBlockId}-pp-is-required`}
            mutate={async (v) => {
              const params: UpdateBlockParams = {
                id: block.id,
                instanceChildTarget: block.instanceChildTarget,
                data: {
                  constraints: { isRequired: v },
                },
                path,
                strategy: 'merge-deep',
                isoNow: new Date().toISOString(),
              }
              const result = await replicache?.mutate.updateBlock(params)
              if (result?.undo) {
                undoManager.current?.push({
                  redo: [
                    getInputFocusOp(`el-${sourceBlockId}-pp-is-required`),
                    { op: 'updateBlock', args: params },
                  ],
                  undo: [
                    getInputFocusOp(`el-${sourceBlockId}-pp-is-required`),
                    {
                      op: 'blockBatchMutations',
                      args: { mutations: result.undo },
                    },
                  ],
                })
              }
            }}
          />
          <PropertiesPanel.CheckList
            options={iconLibraries}
            checkedOptions={allowedLibraries}
            id={`el-${sourceBlockId}-pp-allowed-libraries`}
            mutate={async (checked, newOptions) => {
              let newAllowedLibraries: typeof block.constraints.allowedLibraries =
                {}
              if (typeof checked === 'boolean') {
                // cleared or selected one option path
                newOptions.forEach((o) => {
                  if (!newAllowedLibraries) {
                    newAllowedLibraries = { [o.id]: checked }
                  } else newAllowedLibraries[o.id] = checked
                })
              } else {
                // cleared or selected all options path
                if (newOptions.length === iconLibraries.length) {
                  newAllowedLibraries = null
                } else {
                  newAllowedLibraries = {}
                  iconLibraries.forEach((o) => {
                    if (!newAllowedLibraries) {
                      newAllowedLibraries = {}
                    }
                    newAllowedLibraries[o.id] = false
                  })
                }
              }

              if (newAllowedLibraries) {
                const allowedFromConstraintsAsArray =
                  Object.values(newAllowedLibraries)
                if (
                  allowedFromConstraintsAsArray.length === iconLibraries.length
                ) {
                  const allLibsAllowed = allowedFromConstraintsAsArray.every(
                    (value) => value
                  )
                  if (allLibsAllowed) {
                    newAllowedLibraries = null
                  }
                }
              }

              const params: UpdateBlockParams = {
                id: block.id,
                instanceChildTarget: block.instanceChildTarget,
                data: {
                  constraints: { allowedLibraries: newAllowedLibraries },
                  type: 'icon',
                },
                path,
                strategy: 'merge-deep',
                isoNow: new Date().toISOString(),
              }
              const result = await replicache?.mutate.updateBlock(params)
              if (result?.undo) {
                undoManager.current?.push({
                  redo: [
                    getInputFocusOp(`el-${sourceBlockId}-pp-is-required`),
                    { op: 'updateBlock', args: params },
                  ],
                  undo: [
                    getInputFocusOp(`el-${sourceBlockId}-pp-is-required`),
                    {
                      op: 'blockBatchMutations',
                      args: { mutations: result.undo },
                    },
                  ],
                })
              }
            }}
            title="Allowed libraries"
          />
        </PropertiesPanel.Group>

        <PropertiesPanel.CommonSearchConfigGroup
          block={block}
          parent={parent}
          path={path}
        />
      </PropertiesPanel.PropertiesContainer>
    </>
  )
}
